import React, { useState, useMemo } from "react";
import { SetterScorecard } from "./SetterScorecard";
import { SetterMetrics } from "../../hooks/useSetterPerformance";
import { useReplyTurnDistributionAll } from "../../hooks/useReplyTurnDistributionAll";
import { useSpeedToLead } from "../../hooks/useSpeedToLead";
import Card from "../ui/Card";
import type { ProofMetricType } from "../../types/core";

interface SetterScorecardGridProps {
  metrics: SetterMetrics[];
  isLoading: boolean;
  dayCount: number;
  onMetricClick: (metricType: ProofMetricType, setterId?: string) => void;
  // Work pattern inline props (passed through to each scorecard)
  organizationId: string;
  setterIds: string[];
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
}

export const SetterScorecardGrid: React.FC<SetterScorecardGridProps> = ({
  metrics,
  isLoading,
  dayCount,
  onMetricClick,
  organizationId,
  setterIds,
  dateRange,
  timezone,
}) => {
  const [activeSetterIdx, setActiveSetterIdx] = useState(0);

  // Reply Turns card right-click → in-place window flip. The window distribution
  // is fetched ONCE for ALL setters the first time any card is right-clicked
  // (the hook short-circuits to [] while setterIds is empty, so this costs
  // nothing until used). Mode state lives HERE so one right-click flips every
  // setter's card together — per-card divergence was the old S38 behavior,
  // retired by Mako's Jun 11 ruling.
  const [windowRequested, setWindowRequested] = useState(false);
  const [rtCardMode, setRtCardMode] = useState<"lifetime" | "window">("lifetime");
  const handleRtModeFlip = () =>
    setRtCardMode((prev) => {
      const next = prev === "lifetime" ? "window" : "lifetime";
      if (next === "window") setWindowRequested(true);
      return next;
    });
  const { rows: windowRows, loading: windowLoading } = useReplyTurnDistributionAll({
    organizationId,
    setterIds: windowRequested ? setterIds : [],
    dateRange,
    windowMode: "window",
  });
  const windowBySetter = useMemo(() => {
    const m = new Map<string, (typeof windowRows)[number]>();
    for (const r of windowRows) m.set(r.setterId, r);
    return m;
  }, [windowRows]);

  // Speed to Lead — eager, standalone fetch (S40). Hydrates independently; a hung
  // RPC here does NOT hold the rest of the scorecard hostage (S38 lesson).
  const { rows: speedRows, loading: speedLoading } = useSpeedToLead({
    organizationId,
    setterIds,
    dateRange,
    timezone,
  });
  const speedMap = useMemo(() => new Map(speedRows.map((r) => [r.setterId, r])), [speedRows]);

  // If loading AND we have no data yet, show a minimal loading indicator (not empty card shells).
  // Empty white card rectangles look broken. A single centered spinner is cleaner.
  if ((!metrics || metrics.length === 0) && isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400" />
        <span className="ml-3 token-14 text-glass-muted">Loading setter data...</span>
      </div>
    );
  }

  // If loading but we HAVE stale data, show the stale data (no skeleton swap)
  // This is true stale-while-revalidate at the UI level

  // Empty state (no data and not loading)
  if (!metrics || metrics.length === 0) {
    return (
      <Card className="p-8">
        <div className="flex flex-col items-center justify-center gap-3 py-4">
          <svg
            className="w-10 h-10 text-glass-muted opacity-40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
            />
          </svg>
          <p className="token-14 text-glass-muted">No setter activity in this period</p>
          <p className="token-11 text-glass-inactive">
            Setter data aggregates from GHL DMs — check that sync has run
          </p>
        </div>
      </Card>
    );
  }

  const scorecardProps = (m: SetterMetrics) => ({
    metrics: m,
    dayCount,
    onMetricClick,
    setterId: m.setter_id,
    organizationId,
    setterIds,
    // Inherited from `metrics`, never filtered. `metrics` IS the set already
    // rendered as scorecards on this page, so the modal switcher can never show
    // a setter the page does not. SetterMetrics carries no active/status field,
    // so there is nothing client-side to filter on even if we wanted to.
    roster: metrics.map((s) => ({ id: s.setter_id, name: s.setter_name })),
    dateRange,
    timezone,
    windowData: windowBySetter.get(m.setter_id),
    rtCardMode,
    onRtModeFlip: handleRtModeFlip,
    windowLoading: windowRequested && windowLoading,
    speedToLead: speedMap.get(m.setter_id),
    speedToLeadLoading: speedLoading,
  });

  // Single setter: centered with large max-width for prominence
  if (metrics.length === 1) {
    return (
      <div className="anim-card-in max-w-5xl mx-auto" data-testid="setter-scorecard-grid">
        <SetterScorecard {...scorecardProps(metrics[0])} />
      </div>
    );
  }

  // Two setters: side-by-side with generous spacing
  if (metrics.length === 2) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="setter-scorecard-grid">
          {metrics.map((m, idx) => (
            <div
              key={m.setter_id}
              className="anim-card-in"
              style={{ animationDelay: `${idx * 90}ms` }}
            >
              <SetterScorecard {...scorecardProps(m)} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Three setters: side-by-side columns on desktop (no tab cycling), tabs on mobile
  if (metrics.length === 3) {
    const activeSetter = metrics[Math.min(activeSetterIdx, metrics.length - 1)];
    return (
      <div className="space-y-4" data-testid="setter-scorecard-grid">
        {/* Mobile / tablet: tab bar + single active card */}
        <div className="lg:hidden space-y-4">
          <div className="bg-black/[0.05] rounded-lg p-1 flex gap-1 flex-wrap">
            {metrics.map((m, idx) => (
              <button
                key={m.setter_id}
                data-testid="scorecard-setter-tab"
                onClick={() => setActiveSetterIdx(idx)}
                className={`anim-tap px-4 py-2 rounded-md token-14 font-medium transition-colors ${
                  idx === activeSetterIdx
                    ? "bg-black/[0.08] text-glass shadow-sm"
                    : "text-glass-secondary hover:text-glass"
                }`}
              >
                {m.setter_name}
              </button>
            ))}
          </div>
          {/* Keyed by setter so each tab switch re-mounts → .anim-card-in bounce */}
          <div key={activeSetter.setter_id} className="anim-card-in max-w-4xl mx-auto">
            <SetterScorecard {...scorecardProps(activeSetter)} />
          </div>
        </div>

        {/* Desktop: all three side-by-side, staggered entrance left → right */}
        <div className="hidden lg:grid lg:grid-cols-3 lg:gap-6">
          {metrics.map((m, idx) => (
            <div
              key={m.setter_id}
              className="anim-card-in"
              style={{ animationDelay: `${idx * 90}ms` }}
            >
              <SetterScorecard {...scorecardProps(m)} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 4+ setters: tab bar interface with centered, prominent card
  // (4 columns at 1280px would be ~280px each — too cramped for the dense scorecard layout)
  const activeSetter = metrics[Math.min(activeSetterIdx, metrics.length - 1)];

  return (
    <div className="space-y-4" data-testid="setter-scorecard-grid">
      {/* Tab bar */}
      <div className="bg-black/[0.05] rounded-lg p-1 flex gap-1 flex-wrap">
        {metrics.map((m, idx) => (
          <button
            key={m.setter_id}
            data-testid="scorecard-setter-tab"
            onClick={() => setActiveSetterIdx(idx)}
            className={`anim-tap px-4 py-2 rounded-md token-14 font-medium transition-colors ${
              idx === activeSetterIdx
                ? "bg-black/[0.08] text-glass shadow-sm"
                : "text-glass-secondary hover:text-glass"
            }`}
          >
            {m.setter_name}
          </button>
        ))}
      </div>

      {/* Active setter card - centered with max-width for prominence.
          Keyed by setter so each tab switch re-mounts → .anim-card-in bounce */}
      <div key={activeSetter.setter_id} className="anim-card-in max-w-4xl mx-auto">
        <SetterScorecard {...scorecardProps(activeSetter)} />
      </div>
    </div>
  );
};
